import React, { useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../context/ContractContext';
import { useNotify } from '../../context/NotifyContext';
import Card from '../common/Card';
import Button from '../common/Button';
import Loader from '../common/Loader';
import InlineLoader from '../common/InlineLoader';
import { fromBN, toBN, formatUSDT } from '../../utils/format';
import { parseError } from '../../utils/errors';

export default function InvestForm({ onSuccess, initialRefId = '', lockRefId = false }) {
  const { account, wrongNetwork } = useWallet();
  const { main, mainW, usdt, usdtW, token2W, addresses } = useContracts();
  const { notify } = useNotify();

  const [uid, setUid] = useState('0');
  const [refId, setRefId] = useState(initialRefId || '');
  const [amount, setAmount] = useState('');
  const [cfg, setCfg] = useState({ min: 50, mult: 50, price: 1 });
  const [balUsdt, setBalUsdt] = useState(ethers.BigNumber.from(0));
  const [loading, setLoading] = useState(true);
  const [txStep, setTxStep] = useState(null);
  const mountedRef = useRef(true);

  const isSameToken =
    addresses.token2 &&
    addresses.usdt &&
    addresses.token2.toLowerCase() === addresses.usdt.toLowerCase();

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const [_uid, _min, _mult, _price] = await Promise.all([
          account ? main.addrToId(account) : Promise.resolve(ethers.BigNumber.from(0)),
          main.minPackageAmount(),
          main.packageMultiple(),
          main.token2PriceInUSDT()
        ]);
        let bu = ethers.BigNumber.from(0);
        if (account) {
          try { bu = await usdt.balanceOf(account); } catch (_) {}
        }
        if (!mountedRef.current) return;
        setUid(_uid.toString());
        setCfg({
          min: fromBN(_min),
          mult: fromBN(_mult),
          price: fromBN(_price)
        });
        setBalUsdt(bu);
        setLoading(false);
      } catch (err) {
        if (mountedRef.current) { setLoading(false); }
      }
    };
    setLoading(true);
    load();
    return () => { mountedRef.current = false; };
  }, [account, main, usdt, txStep]);

  const isRegistered = uid !== '0';
  const amtNum = Number(amount) || 0;

  const validate = () => {
    if (!account) { notify('error', 'Connect your wallet first'); return false; }
    if (wrongNetwork) { notify('error', 'Switch to the correct network'); return false; }
    if (amtNum < cfg.min) { notify('error', `Minimum is $${cfg.min} USDT`); return false; }
    if (amtNum % cfg.mult !== 0) { notify('error', `Amount must be multiple of $${cfg.mult}`); return false; }
    if (!isRegistered && (!refId || Number(refId) <= 0)) {
      notify('warning', 'No referrer ID provided — using default referrer');
    }
    return true;
  };

  const approveIfNeeded = async (contract, needed) => {
    const allowance = await contract.allowance(account, addresses.cryptoPay);
    if (allowance.gte(needed)) return;
    setTxStep('Approving USDT');
    const tx = await contract.approve(addresses.cryptoPay, ethers.constants.MaxUint256);
    await tx.wait();
  };

  const doInvest = async () => {
    if (!validate()) return;
    try {
      const amtBN = toBN(amtNum);
      const usdtHalfBN = amtBN.div(2);
      const t2HalfBN = amtBN.sub(usdtHalfBN).mul(ethers.utils.parseUnits('1', 18)).div(toBN(cfg.price));
      const totalUsdtNeeded = isSameToken ? usdtHalfBN.add(t2HalfBN) : usdtHalfBN;

      if (balUsdt.lt(totalUsdtNeeded)) {
        notify('error', `Insufficient USDT balance. Need $${formatUSDT(totalUsdtNeeded)}`);
        return;
      }

      await approveIfNeeded(usdtW, totalUsdtNeeded);
      if (!isSameToken && token2W) {
        await approveIfNeeded(token2W, t2HalfBN);
      }

      setTxStep(isRegistered ? 'Submitting investment' : 'Registering & investing');
      let tx;
      if (isRegistered) {
        tx = await mainW.invest(amtBN);
      } else {
        const rid = refId && Number(refId) > 0 ? refId : '0';
        tx = await mainW.registerAndInvest(rid, amtBN);
      }
      setTxStep('Waiting for confirmation');
      await tx.wait();

      notify('success', `Investment of $${amount} USDT confirmed!`);
      setAmount('');
      setTxStep(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      setTxStep(null);
      notify('error', parseError(err));
    }
  };

  if (loading) return <Card title="Invest"><InlineLoader text="Loading..." /></Card>;

  return (
    <>
      {txStep && <Loader text={txStep} subtext="Please confirm in your wallet" />}
      <Card title={isRegistered ? 'New Investment' : 'Register & Invest'} glow className="form-card">
        {!isRegistered && (
          <div className="input-group">
            <label className="input-label">Referrer ID {lockRefId && initialRefId ? '(from link)' : '(Optional)'}</label>
            <input
              className="input"
              type="number"
              placeholder="Enter referrer ID or leave blank"
              value={refId}
              onChange={(e) => setRefId(e.target.value)}
              readOnly={lockRefId && !!initialRefId}
              style={lockRefId && initialRefId ? { opacity: 0.75 } : undefined}
            />
            <div className="input-hint">
              {lockRefId && initialRefId
                ? `Referred by ID #${initialRefId}`
                : 'Leave blank to use default referrer'}
            </div>
          </div>
        )}
        <div className="input-group">
          <label className="input-label">Investment Amount (USDT)</label>
          <input
            className="input"
            type="number"
            step={cfg.mult}
            min={cfg.min}
            placeholder={`Min $${cfg.min}, multiples of $${cfg.mult}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <div className="input-hint">
            Min: ${cfg.min} · Step: ${cfg.mult}
          </div>
        </div>

        {amtNum > 0 && (
          <div className="form-summary">
            <div><span className="sum-key">Investment:</span> <span className="sum-val">${amtNum.toLocaleString()} USDT</span></div>
            <div><span className="sum-key">Package Type:</span> <span className="sum-val">Standard</span></div>
          </div>
        )}

        <div className="mt-4">
          <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>YOUR USDT BALANCE</div>
          <div className="mono text-primary" style={{ fontSize: 20, fontWeight: 700 }}>
            ${formatUSDT(balUsdt)}
          </div>
        </div>

        <div className="mt-6">
          <Button
            block
            variant="primary"
            size="lg"
            onClick={doInvest}
            disabled={!!txStep || !account || wrongNetwork}
          >
            {isRegistered ? 'INVEST NOW' : 'REGISTER & INVEST'}
          </Button>
        </div>
      </Card>
    </>
  );
}
